import { useCallback, useEffect, useRef } from 'react'
import type { MapRef } from 'react-map-gl/mapbox'
import useDebouncedCallback from 'hooks/useDebouncedCallback'

const RESIZE_DEBOUNCE_MS = 16
/**
 * Module scope: the hook memoises on this object's identity. `maxWait` is what keeps the map up
 * during a drag, where a trailing debounce never settles until the traveller lets go.
 */
const RESIZE_DEBOUNCE_OPTIONS = { maxWait: 100 }

export function useMapResize(
  mapRef: React.RefObject<MapRef | null>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  isViewportActive = true,
): void {
  const wasViewportActiveRef = useRef(isViewportActive)

  // `resize()` alone anchors the top-left, so the centre slides by whatever width changed.
  const resizeKeepingCentre = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    // A camera already in flight owns the centre; restoring it here would snap the flight back.
    if (map.isEasing()) {
      map.resize()
      return
    }
    const centre = map.getCenter()
    map.resize()
    map.setCenter(centre)
  }, [mapRef])

  const debouncedResize = useDebouncedCallback(
    resizeKeepingCentre,
    RESIZE_DEBOUNCE_MS,
    [resizeKeepingCentre],
    RESIZE_DEBOUNCE_OPTIONS,
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(debouncedResize)
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [containerRef, debouncedResize])

  useEffect(() => {
    const becameActive = isViewportActive && !wasViewportActiveRef.current
    wasViewportActiveRef.current = isViewportActive
    if (!becameActive) return
    resizeKeepingCentre()
  }, [isViewportActive, resizeKeepingCentre])
}
